import React from 'react';
import { Box, Typography, IconButton, Fade, Link, Button } from '@mui/material';
import { RefreshOutlined, CheckOutlined, AccessTimeOutlined, ErrorOutlineOutlined, EmojiEventsOutlined } from '@mui/icons-material';
import { Message } from '../types';
import { format, isToday, isYesterday } from 'date-fns';
import ChalkboardSpecials from './ChalkboardSpecials';

interface MessageBubbleProps {
  message: Message;
  onRetry?: (messageId: string) => void;
  onLinkClick?: (url: string) => void;
  onOptInClick?: () => void; // Callback to open opt-in modal
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onRetry, onLinkClick, onOptInClick }) => {
  const isPatron = message.sender === 'patron';
  const isSystem = message.content.type === 'system';
  const isTableChatOptIn = message.metadata?.isOptInRequest && message.metadata?.optInCollectionType === 'tablechat';
  const isThirdPartyOptIn = message.metadata?.isOptInRequest && message.metadata?.optInCollectionType === 'thirdParty';
  const isOptInPromoCode = message.metadata?.isOptInPromoCode;

  // URL detection and linkify function
  const linkifyText = (text: string) => {
    // Comprehensive regex to detect all types of URLs that users might paste
    const urlRegex = /(https?:\/\/[^\s]+|ftp:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part && urlRegex.test(part)) {
        // Determine the correct href
        let href = part;
        if (!part.startsWith('http') && !part.startsWith('ftp')) {
          href = `https://${part}`;
        }
        
        return (
          <Link
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              // Log the click for reporting (silent, doesn't block the link)
              onLinkClick?.(href);
            }}
            sx={{
              color: 'inherit',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'underline',
                opacity: 0.8
              }
            }}
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  const formatTimestamp = (timestamp: Date) => {
    if (isToday(timestamp)) {
      return format(timestamp, 'HH:mm');
    } else if (isYesterday(timestamp)) {
      return `Yesterday ${format(timestamp, 'HH:mm')}`;
    } else {
      return format(timestamp, 'MMM d, HH:mm');
    }
  };

  const getStatusIcon = () => {
    switch (message.status) {
      case 'sending':
        return <AccessTimeOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />;
      case 'sent':
      case 'delivered':
        return <CheckOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />;
      case 'failed':
        return (
          <IconButton 
            size="small" 
            onClick={() => onRetry?.(message.id)}
            sx={{ p: 0, ml: 0.5 }}
          >
            <RefreshOutlined sx={{ fontSize: 14, color: 'error.main' }} />
          </IconButton>
        );
      default:
        return null;
    }
  };

  if (message.metadata?.isDailySpecials) {
    return (
      <Fade in timeout={300}>
        <Box data-message="true">
          <ChalkboardSpecials 
            content={message.content.text} 
            timestamp={message.timestamp}
          />
        </Box>
      </Fade>
    );
  }

  // TableChat Opt-In Request - render as normal chat bubble with "Join Now" link (opens opt-in modal)
  if (isTableChatOptIn) {
    return (
      <Fade in timeout={300}>
        <Box
          data-message="true"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 0.75,
            px: 2
          }}
        >
          <Box sx={{ maxWidth: '75%', minWidth: '60px' }}>
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.mode === 'light' ? '#e0e0e0' : '#424242',
                color: (theme) => theme.palette.text.primary,
                borderRadius: '18px',
                px: 2,
                py: 1.5,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="body1" sx={{ lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {message.content.text}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={onOptInClick}
                  sx={{
                    background: 'linear-gradient(135deg, #0891B2, #0e7490)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 4,
                    py: 0.75,
                    boxShadow: '0 2px 6px rgba(8, 145, 178, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0e7490, #155e75)',
                      boxShadow: '0 3px 8px rgba(8, 145, 178, 0.4)',
                    }
                  }}
                >
                  Join
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5, px: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 'clamp(0.65rem, 3vw, 0.85rem)' }}>
                {formatTimestamp(message.timestamp)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    );
  }

  /* FUTURE USE: TableChat Opt-In fancy promotional card (commented out in favor of normal chat bubble above)
  if (isTableChatOptIn) {
    return (
      <Fade in timeout={300}>
        <Box
          data-message="true"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 2,
            px: 2
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0891B2 0%, #0e7490 100%)',
              borderRadius: 3,
              p: 2.5,
              width: '100%',
              maxWidth: '340px',
              boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }}
              >
                <CelebrationOutlined sx={{ fontSize: 28, color: 'white' }} />
              </Box>
            </Box>
            <Typography variant="body1" sx={{ color: 'white', textAlign: 'center', fontWeight: 500, lineHeight: 1.5, mb: 2 }}>
              {message.content.text}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={onOptInClick}
              sx={{
                backgroundColor: 'white',
                color: '#0891B2',
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
              }}
            >
              Join Now
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  }
  */

  // 3rd Party Opt-In Request - render as normal chat bubble with "Join Now" link (opens external URL)
  if (isThirdPartyOptIn) {
    const thirdPartyUrl = message.metadata?.optInThirdPartyUrl || '';
    const rawText = message.content.text || '';
    const displayText = thirdPartyUrl
      ? rawText.replace(new RegExp('\\s*' + thirdPartyUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i'), '').trim()
      : rawText.replace(/\s*(https?:\/\/[^\s]+)\s*$/, '').trim();

    const handleJoinClick = () => {
      if (thirdPartyUrl) {
        let url = thirdPartyUrl;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        onLinkClick?.(url);
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <Fade in timeout={300}>
        <Box
          data-message="true"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 0.75,
            px: 2
          }}
        >
          <Box sx={{ maxWidth: '75%', minWidth: '60px' }}>
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.mode === 'light' ? '#e0e0e0' : '#424242',
                color: (theme) => theme.palette.text.primary,
                borderRadius: '18px',
                px: 2,
                py: 1.5,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="body1" sx={{ lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {displayText}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={handleJoinClick}
                  sx={{
                    background: 'linear-gradient(135deg, #0891B2, #0e7490)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 4,
                    py: 0.75,
                    boxShadow: '0 2px 6px rgba(8, 145, 178, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0e7490, #155e75)',
                      boxShadow: '0 3px 8px rgba(8, 145, 178, 0.4)',
                    }
                  }}
                >
                  Join
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5, px: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 'clamp(0.65rem, 3vw, 0.85rem)' }}>
                {formatTimestamp(message.timestamp)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    );
  }

  /* FUTURE USE: Third-party Opt-In fancy promotional card (commented out in favor of normal chat bubble above)
  if (isThirdPartyOptIn) {
    const thirdPartyUrl = message.metadata?.optInThirdPartyUrl || '';
    const rawText = message.content.text || '';
    const displayText = thirdPartyUrl
      ? rawText.replace(new RegExp('\\s*' + thirdPartyUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i'), '').trim()
      : rawText.replace(/\s*(https?:\/\/[^\s]+)\s*$/, '').trim();
    const handleJoinClick = () => {
      if (thirdPartyUrl) {
        let url = thirdPartyUrl;
        if (!url.startsWith('http://') && !url.startsWith('https://')) { url = 'https://' + url; }
        onLinkClick?.(url);
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };
    return (
      <Fade in timeout={300}>
        <Box data-message="true" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2, px: 2 }}>
          <Box sx={{ background: 'linear-gradient(135deg, #0891B2 0%, #0e7490 100%)', borderRadius: 3, p: 2.5, width: '100%', maxWidth: '340px', boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)' }}>
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <CelebrationOutlined sx={{ fontSize: 28, color: 'white' }} />
              </Box>
            </Box>
            <Typography variant="body1" sx={{ color: 'white', textAlign: 'center', fontWeight: 500, lineHeight: 1.5, mb: 2 }}>{displayText}</Typography>
            <Button fullWidth variant="contained" onClick={handleJoinClick} sx={{ backgroundColor: 'white', color: '#0891B2', fontWeight: 600, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }}>Join Now</Button>
          </Box>
        </Box>
      </Fade>
    );
  }
  */

  // VIP Promo Code Award Card - "Red Carpet VIP" style with gold accents
  if (isOptInPromoCode) {
    const promoCode = message.metadata?.promoCode || '';
    return (
      <Fade in timeout={300}>
        <Box
          data-message="true"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 2,
            px: 2
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #C41E3A 0%, #9B1B30 50%, #6B0F23 100%)',
              borderRadius: 3,
              p: 3,
              width: '100%',
              maxWidth: '340px',
              boxShadow: '0 8px 24px rgba(196, 30, 58, 0.5), 0 0 0 2px rgba(255, 215, 0, 0.25) inset',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle sparkle effect */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 30% 20%, rgba(255,215,0,0.15) 0%, transparent 50%)',
                pointerEvents: 'none'
              }}
            />
            
            {/* Trophy Icon - gold */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,215,0,0.2)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  border: '2px solid rgba(255,215,0,0.35)'
                }}
              >
                <EmojiEventsOutlined sx={{ fontSize: 44, color: '#FFD700' }} />
              </Box>
            </Box>
            
            {/* Main Title - gold text */}
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#FFD700',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '1.75rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 1
              }}
            >
              NEW VIP MEMBER!
            </Typography>
            
            {/* Welcome Text */}
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255,255,255,0.95)',
                textAlign: 'center',
                fontWeight: 500,
                mb: 1
              }}
            >
              Welcome to the club!
            </Typography>
            
            {/* Redemption Instructions */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.85)',
                textAlign: 'center',
                fontWeight: 400,
                fontStyle: 'italic',
                mb: 3
              }}
            >
              Show this to your server to redeem your offer.
            </Typography>
            
            {/* Divider - gold tinted */}
            <Box 
              sx={{ 
                borderTop: '1px solid rgba(255,215,0,0.3)',
                mx: 2,
                mb: 2
              }} 
            />
            
            {/* Promo Code with date for fraud prevention */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.85)',
                textAlign: 'center',
                display: 'block',
                fontWeight: 400,
                letterSpacing: '0.5px'
              }}
            >
              {promoCode}
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }

  // System messages (centered, gray)
  if (isSystem) {
    return (
      <Fade in timeout={300}>
        <Box
          data-message="true"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 2,
            px: 2
          }}
        >
          <Box
            sx={{
              backgroundColor: (theme) => 
                theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
              borderRadius: 3,
              px: 2,
              py: 1,
              maxWidth: '80%',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: 'clamp(0.875rem, 4vw, 1.1rem)', // Responsive to user text size
                lineHeight: 1.4
              }}
            >
              {linkifyText(message.content.text)}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.disabled',
                fontSize: 'clamp(0.65rem, 3vw, 0.85rem)', // Responsive timestamp
                mt: 0.5,
                display: 'block'
              }}
            >
              {formatTimestamp(message.timestamp)}
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box
        data-message="true"
        sx={{
          display: 'flex',
          justifyContent: isPatron ? 'flex-end' : 'flex-start',
          mb: 0.75,
          px: 2
        }}
      >
        <Box
          sx={{
            maxWidth: '75%',
            minWidth: '60px'
          }}
        >
          {/* Message bubble with optional manager crown badge */}
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block'
            }}
          >
            <Box
              sx={{
                backgroundColor: isPatron 
                  ? (message.senderColor || 'primary.main') 
                  : (theme) => theme.palette.mode === 'light' ? '#e0e0e0' : '#424242', // Adaptive gray for server messages
              color: isPatron ? (
                // Use dark text for bright colors, white text for dark colors
                message.senderColor && ['#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#8bc34a'].includes(message.senderColor)
                  ? '#000000' 
                  : '#ffffff'
              ) : (theme) => theme.palette.text.primary, // Adaptive text on server bubbles
              borderRadius: '18px', // All corners rounded for modern look
              px: 2,
              py: 1.5,
              position: 'relative',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              ...(message.isOptimistic && {
                opacity: 0.7
              }),
              ...(message.status === 'failed' && {
                backgroundColor: isPatron ? 'error.main' : 'error.light',
                color: isPatron ? 'error.contrastText' : 'error.contrastText'
              })
            }}
          >
            {/* Sender name */}
            {message.senderName && (
              <Typography 
                variant="caption"
                sx={{ 
                  display: 'block',
                  mb: 0.5,
                  fontWeight: 600,
                  opacity: 0.8,
                  fontSize: '0.75rem'
                }}
              >
                {message.senderName}
              </Typography>
            )}
            <Typography 
              variant="body1"
              sx={{ 
                lineHeight: 1.4,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
                // Removed fontSize - let theme handle it for consistency
              }}
            >
              {linkifyText(message.content.text)}
            </Typography>

            {/* Error indicator for failed messages */}
            {message.status === 'failed' && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <ErrorOutlineOutlined sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: 'clamp(0.65rem, 3vw, 0.85rem)' }}>
                  Tap to retry
                </Typography>
              </Box>
            )}
            </Box>
            
            {/* Manager crown badge */}
            {(() => {
              const shouldShowCrown = message.senderRole === 'manager' || message.senderRole === 'managerAdmin';
              return shouldShowCrown;
            })() ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: '#ffc107',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  zIndex: 1
                }}
              >
                <Box
                  sx={{
                    fontSize: '10px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  ★
                </Box>
              </Box>
            ) : null}
          </Box>

          {/* Timestamp and status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isPatron ? 'flex-end' : 'flex-start',
              mt: 0.5,
              px: 1
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.disabled',
                fontSize: 'clamp(0.65rem, 3vw, 0.85rem)' // Responsive timestamp
              }}
            >
              {formatTimestamp(message.timestamp)}
            </Typography>
            
            {/* Status icon for patron messages */}
            {isPatron && (
              <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                {getStatusIcon()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default MessageBubble;
